import { ReactNode } from 'react';
import { BaseIssue } from 'valibot';

import { faPipe } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	FormHelperText,
	InputAdornment,
	OutlinedInput,
	SxProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import { useFieldContext } from '#form';

import { LabelInput } from '@repo/ui/LabelInput';
import { isSxArray } from '@repo/ui/utils/sxUtils';

interface InputProps {
	label: ReactNode;
	subLabel?: string;
	labelEnd?: ReactNode;
	ariaLabel?: string;
	sx?: SxProps<Theme>;
	numeric?: true;
	multiline?: true;
	rows?: number;
	maxLength?: number;
	showCount?: true;
	endField?: ReactNode;
	startAdornment?: ReactNode;
	endAdornment?: ReactNode;
	disabled?: boolean;
	optional?: boolean;
}

const defaultSx = {};

export function TextInput({
	label,
	subLabel,
	labelEnd,
	ariaLabel,
	numeric,
	multiline,
	rows,
	maxLength,
	showCount,
	endField,
	startAdornment,
	endAdornment,
	disabled,
	optional,
	sx = defaultSx,
}: InputProps) {
	const field = useFieldContext<string | number | null | undefined>();
	const error = (
		field.state.meta as {
			errors?: Array<string | BaseIssue<unknown>>;
		}
	).errors?.[0];

	return (
		<LabelInput
			label={label}
			subLabel={subLabel}
			fieldName={field.name}
			labelEnd={optional ? <LabelInput.Optional /> : labelEnd}
		>
			<OutlinedInput
				multiline={multiline}
				rows={rows}
				disabled={disabled}
				id={field.name}
				name={field.name}
				value={field.state.value ?? ''}
				onBlur={() => {
					if (typeof field.state.value === 'string') {
						field.handleChange(field.state.value.trim());
					}
					field.handleBlur();
				}}
				onChange={(e) => {
					if (numeric) {
						field.handleChange(+e.target.value.replace(/\D/g, '') || null);
					} else {
						field.handleChange(e.target.value);
					}
				}}
				error={error != null}
				sx={[
					endField ?
						{
							paddingRight: 0,
						}
					:	null,
					{
						'& .MuiInputAdornment-root .MuiOutlinedInput-notchedOutline': {
							display: 'none',
						},
					},
					...(isSxArray(sx) ? sx : [sx]),
				]}
				{...(endField ?
					{
						endAdornment: (
							<InputAdornment position="end">
								<FontAwesomeIcon color="#aaaaaa" icon={faPipe} size="lg" />
								{endField}
							</InputAdornment>
						),
					}
				:	{
						...(startAdornment ?
							{
								startAdornment: (
									<InputAdornment position="start">
										{startAdornment}
									</InputAdornment>
								),
							}
						:	{}),
						...(endAdornment ?
							{
								endAdornment: (
									<InputAdornment position="end">{endAdornment}</InputAdornment>
								),
							}
						:	{}),
					})}
				inputProps={{
					...(maxLength ? { maxLength } : {}),
					'aria-label': ariaLabel,
				}}
			/>
			<FormHelperText error={error != null}>
				{error ?
					typeof error === 'string' ?
						error
					:	error.message
				: showCount ?
					field.state.value ?
						`${String(field.state.value).length}/${maxLength}`
					:	`${maxLength} characters max`
				:	undefined}
			</FormHelperText>
		</LabelInput>
	);
}
